import type { Tag, TagType } from '@/types/tag';

export const LOCATION_TAG_COLORS = [
  '#34D399',
  '#60A5FA',
  '#F472B6',
  '#F59E0B',
  '#A78BFA',
  '#22D3EE',
  '#FB7185',
  '#4ADE80',
  '#38BDF8',
  '#FBBF24',
  '#C084FC',
  '#2DD4BF',
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const getDefaultTagColor = (tagType: TagType, seed: string): string | undefined => {
  if (tagType !== 'location') return undefined;
  const index = hashString(seed) % LOCATION_TAG_COLORS.length;
  return LOCATION_TAG_COLORS[index];
};

export const getTagDisplayColor = (tag: Pick<Tag, 'tagType' | 'color' | 'name' | 'id'>): string | undefined => {
  if (tag.tagType !== 'location') return undefined;
  return tag.color || getDefaultTagColor('location', tag.name || tag.id);
};

/**
 * WCAG relative luminance for an sRGB hex string (`#rgb` or `#rrggbb`). Returns
 * 0 on parse failure, which routes unknown inputs to white text via
 * {@link getReadableTextColor} — matching the prior hardcoded behavior so no
 * caller ever gets *worse* contrast than before this util existed.
 */
export const hexToLuminance = (hex: string): number => {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return 0;
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = toLin(parseInt(h.slice(0, 2), 16));
  const g = toLin(parseInt(h.slice(2, 4), 16));
  const b = toLin(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const DARK_TEXT = '#1a1a1a';
const LIGHT_TEXT = '#ffffff';
// Precomputed luminance of the two candidate text colors.
const DARK_TEXT_LUMINANCE = hexToLuminance(DARK_TEXT); // ≈0.0103
const LIGHT_TEXT_LUMINANCE = 1; // pure white

/** WCAG contrast ratio between two relative luminances (order-independent). */
const contrastRatio = (a: number, b: number): number =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/**
 * Pick the more readable of dark/white text for content painted on solid `bg`.
 *
 * Chooses by *measured WCAG contrast ratio*, not a luminance threshold. A fixed
 * threshold (e.g. `luminance > 0.5 → dark`) misclassifies the saturated mid
 * colors that dominate LOCATION_TAG_COLORS: emerald `#34D399` (lum ≈0.50),
 * blue `#60A5FA` (≈0.36) and pink `#F472B6` (≈0.35) all sit below 0.5 yet need
 * *dark* text — white text scores only ~1.9–2.5:1 on them, well under AA. The
 * old hardcoded `#ffffff` was in fact failing AA across essentially the whole
 * location palette. Comparing both candidates' contrast and taking the winner
 * is correct for any color the picker can produce, now or later, with no
 * palette-specific tuning. See tagColors.test.ts, which proves AA for every
 * palette member.
 */
export const getReadableTextColor = (bg: string): string => {
  const bgLum = hexToLuminance(bg);
  return contrastRatio(bgLum, DARK_TEXT_LUMINANCE) >= contrastRatio(bgLum, LIGHT_TEXT_LUMINANCE)
    ? DARK_TEXT
    : LIGHT_TEXT;
};
